import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { TrustBadge } from "@/components/TrustBadge";

export default function Profile() {
  const { id } = useParams();
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [threads, setThreads] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ bio: "", location: "" });
  const [saving, setSaving] = useState(false);

  const isMe = user?.user_id === id;

  useEffect(() => {
    api.get(`/users/${id}`).then(({ data }) => {
      setProfile(data);
      setForm({ bio: data.bio || "", location: data.location || "" });
    });
    api.get("/listings", { params: { seller_id: id } }).then(({ data }) => setListings(data));
    api.get("/threads").then(({ data }) => setThreads(data.filter((t) => t.author_id === id)));
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/users/me", form);
      setProfile(data);
      setUser(data);
      toast.success("Profile updated");
      setEditing(false);
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return <div className="max-w-4xl mx-auto px-4 py-16 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in" data-testid="profile-page">
      <Card className="bg-card">
        <CardContent className="p-6 md:p-8 flex flex-col md:flex-row gap-6 items-start">
          <Avatar className="w-24 h-24 border-4 border-background shadow-md">
            <AvatarImage src={profile.picture} />
            <AvatarFallback className="text-2xl">{profile.name?.[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            {!editing ? (
              <>
                <h1 className="font-display font-extrabold text-3xl">{profile.name}</h1>
                <div className="mt-1 text-sm text-muted-foreground inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {profile.location || "Florida"}</div>
                {profile.bio && <p className="mt-4 text-sm leading-relaxed max-w-2xl">{profile.bio}</p>}
              </>
            ) : (
              <div className="space-y-3 max-w-lg">
                <div>
                  <Label>Location</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" data-testid="input-profile-location" />
                </div>
                <div>
                  <Label>Bio</Label>
                  <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1 min-h-[100px]" data-testid="input-profile-bio" />
                </div>
              </div>
            )}
          </div>
          {isMe && (
            <div>
              {editing ? (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setEditing(false)} data-testid="btn-cancel-edit">Cancel</Button>
                  <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="btn-save-profile">
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save
                  </Button>
                </div>
              ) : (
                <Button variant="outline" onClick={() => setEditing(true)} data-testid="btn-edit-profile">Edit profile</Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {profile.trust && (
        <div className="mt-6">
          <TrustBadge trust={profile.trust} />
        </div>
      )}

      <Tabs defaultValue="listings" className="mt-8">
        <TabsList className="bg-muted">
          <TabsTrigger value="listings" data-testid="tab-listings">Listings ({listings.length})</TabsTrigger>
          <TabsTrigger value="threads" data-testid="tab-threads">Threads ({threads.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="listings" className="mt-6">
          {listings.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-10">No listings.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((l) => (
                <Link key={l.listing_id} to={`/listings/${l.listing_id}`} data-testid={`profile-listing-${l.listing_id}`}>
                  <Card className="overflow-hidden bg-card transition-transform duration-300 hover:-translate-y-1">
                    <div className="relative aspect-[4/3] bg-muted">
                      {l.image_paths?.[0] ? <img src={fileUrl(l.image_paths[0])} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">No photo</div>}
                      {l.status === "sold" && <Badge className="absolute top-2 right-2 bg-foreground text-background">SOLD</Badge>}
                    </div>
                    <CardContent className="p-4">
                      <div className="flex justify-between">
                        <div className="font-semibold text-sm line-clamp-1">{l.title}</div>
                        <div className="font-display font-bold text-secondary text-sm">${l.price.toFixed(0)}</div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="threads" className="mt-6 space-y-3">
          {threads.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-10">No threads.</div>
          ) : threads.map((t) => (
            <Link key={t.thread_id} to={`/threads/${t.thread_id}`} data-testid={`profile-thread-${t.thread_id}`}>
              <Card className="bg-card hover:border-primary/40 transition-colors">
                <CardContent className="p-4">
                  <Badge variant="outline" className="border-accent/40 text-accent bg-accent/5 text-[10px]">{t.category}</Badge>
                  <div className="font-semibold mt-2">{t.title}</div>
                  <div className="text-xs text-muted-foreground line-clamp-1 mt-1">{t.body}</div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
